import { useSyncExternalStore, useEffect } from 'react';
import { Transaction, TransactionType } from '../types/Transaction';
import { Category } from '../types/Category';
import { PaymentMethod } from '../types/PaymentMethod';

/**
 * Os chips de exemplo, com as palavras dela.
 *
 * ── Por que isto existe ───────────────────────────────────────────────────
 *
 * Embaixo de cada campo de texto livre há chips que ensinam o que dá para
 * escrever: "mercado 187,40 no débito", "quanto gastei em delivery?". Eles
 * eram os mesmos para todo mundo. Pedido dela, em 06/10: para quem já tem
 * dados, sugerir com os dados dela — "quanto gastei em Pet?" ensina a mesma
 * gramática, e é uma pergunta que ela de fato faria.
 *
 * ── O que troca, e o que NÃO troca ────────────────────────────────────────
 *
 * Trocam os NOMES: categoria, cartão, o lugar onde ela costuma gastar, a
 * dívida. São palavras que ela mesma escreveu.
 *
 * Os VALORES continuam de exemplo, e isso é a decisão do arquivo. Tocar no
 * chip preenche o campo; um "Uber 23,50 no Nubank" com o valor médio dela
 * pareceria um lançamento que o app está propondo, e bastaria um Enter para
 * virar um gasto que nunca aconteceu. Com "50" redondo, continua sendo
 * visivelmente um exemplo.
 *
 * ── Cada chip ensina uma peça ─────────────────────────────────────────────
 *
 * Os exemplos originais foram escritos para cada um mostrar uma coisa
 * diferente que o campo entende (dia da semana, método, parcelas). A versão
 * pessoal mantém a FORMA de cada frase e só troca o recheio. Quando falta o
 * dado para um recheio, aquela frase volta a ser a genérica — as outras não.
 *
 * ── Nenhum número de dinheiro sai daqui ───────────────────────────────────
 *
 * A soma por categoria abaixo serve só para ORDENAR, e nunca é mostrada. Não
 * é mais uma versão de "gasto do mês" (armadilha 4): é um desempate.
 *
 * As funções são puras; a loja no fim do arquivo é só o encanamento.
 */

/** Com menos lançamentos que isto, os exemplos genéricos ensinam melhor. */
export const MINIMO_PARA_PERSONALIZAR = 5;

/** Quantos dias para trás contam como "o que ela costuma". */
const JANELA_DIAS = 90;

export interface DadosParaExemplos {
  transactions: Transaction[];
  categories: Category[];
  paymentMethods: PaymentMethod[];
}

const MESES = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

const recentes = (ts: Transaction[], hoje: Date): Transaction[] => {
  const corte = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - JANELA_DIAS);
  const iso = `${corte.getFullYear()}-${String(corte.getMonth() + 1).padStart(2, '0')}-${String(corte.getDate()).padStart(2, '0')}`;
  return ts.filter(t => t.type === TransactionType.EXPENSE && String(t.date).slice(0, 10) >= iso);
};

/**
 * As categorias em que ela mais gastou no período, pelo nome.
 *
 * `soVariavel` deixa de fora a conta fixa. Serve ao exemplo de LIMITE: a
 * maior categoria de quase todo mundo é Moradia, e "não quero gastar mais de
 * 500 por mês com Moradia" é um exemplo que ensina a pôr teto no aluguel.
 */
export const categoriasMaisUsadas = (
  d: DadosParaExemplos, hoje: Date, soVariavel = false,
): string[] => {
  const soma = new Map<string, number>();
  for (const t of recentes(d.transactions, hoje)) {
    if (!t.categoryId) continue;
    if (soVariavel && t.isMonthly) continue;
    soma.set(t.categoryId, (soma.get(t.categoryId) ?? 0) + (Number(t.value) || 0));
  }
  return Array.from(soma.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([id]) => d.categories.find(c => c.id === id)?.name)
    .filter((n): n is string => !!n);
};

/**
 * Um nome de lugar serve de exemplo?
 *
 * Curto, sem número e sem pontuação de extrato. "PAG*JoseDaSilva 03/12" é o
 * que vem de uma fatura importada, e num chip vira ruído — pior, ensina que é
 * assim que se escreve.
 */
const serveDeExemplo = (s: string): boolean =>
  s.length >= 3 && s.length <= 18 && !/[\d*/#@]/.test(s);

/** Onde ela mais gasta no dia a dia, como ELA escreve, em minúsculas. */
export const lugaresFrequentes = (d: DadosParaExemplos, hoje: Date): string[] => {
  const vezes = new Map<string, number>();
  for (const t of recentes(d.transactions, hoje)) {
    // Conta fixa e parcela não são "passei no mercado": ficam para o lembrete.
    if (t.isMonthly || t.hasInstallments) continue;
    const nome = String(t.description ?? '').trim().toLowerCase();
    if (!serveDeExemplo(nome)) continue;
    vezes.set(nome, (vezes.get(nome) ?? 0) + 1);
  }
  return Array.from(vezes.entries())
    // Aparecer uma vez só é acaso, não costume.
    .filter(([, n]) => n >= 2)
    .sort((a, b) => b[1] - a[1])
    .map(([nome]) => nome);
};

/** Os métodos que ela mais usa, pelo nome. */
export const metodosMaisUsados = (d: DadosParaExemplos, hoje: Date): string[] => {
  const vezes = new Map<string, number>();
  for (const t of recentes(d.transactions, hoje)) {
    if (!t.paymentMethod) continue;
    vezes.set(t.paymentMethod, (vezes.get(t.paymentMethod) ?? 0) + 1);
  }
  return Array.from(vezes.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([id]) => d.paymentMethods.find(m => m.id === id)?.name)
    .filter((n): n is string => !!n && serveDeExemplo(n));
};

export interface Exemplos {
  lancamento: string[];
  chat: string[];
  busca: string[];
  meta: string[];
}

/** Os de sempre — para quem acabou de chegar, e a base de cada frase. */
export const EXEMPLOS_GENERICOS: Exemplos = {
  lancamento: [
    'terça gastei 50 em uber no crédito',
    'mercado 187,40 no débito',
    'zara 3x de 129,90',
  ],
  chat: [
    'quanto posso gastar esse mês?',
    'posso comprar um sofá de 3000?',
    'quanto gastei em delivery?',
  ],
  busca: [
    'eu gastei algo em pet esse mês?',
    'quais os parcelamentos que fiz em dezembro?',
    'quais foram os recebimentos desse mês',
  ],
  meta: [
    'quero juntar 20 mil pra viagem até dezembro',
    'não quero gastar mais de 500 por mês com delivery pelos próximos 6 meses',
    'reserva de emergência de 30 mil, já tenho 5 mil',
  ],
};

/**
 * Os exemplos desta pessoa.
 *
 * Cada frase mantém a forma da genérica correspondente — mesma posição, mesma
 * peça de gramática — e cai de volta nela quando falta o dado.
 */
export const exemplosPara = (d: DadosParaExemplos, hoje: Date = new Date()): Exemplos => {
  const g = EXEMPLOS_GENERICOS;
  const despesas = d.transactions.filter(t => t.type === TransactionType.EXPENSE);
  if (despesas.length < MINIMO_PARA_PERSONALIZAR) return g;

  const [cat1, cat2] = categoriasMaisUsadas(d, hoje);
  /*
   * Para o exemplo de limite: nunca o que ela marcou como essencial, e de
   * preferência o que marcou como ajustável. Sugerir teto no que ela disse que
   * não tem como mexer é o defeito que a marca de essencial existe para acabar.
   */
  const marca = (nome: string) => d.categories.find(c => c.name === nome)?.essencial;
  const paraLimitar = categoriasMaisUsadas(d, hoje, true).filter(n => marca(n) !== 'essencial');
  const catDoLimite = paraLimitar.find(n => marca(n) === 'ajustavel') ?? paraLimitar[0];
  const [lugar1, lugar2] = lugaresFrequentes(d, hoje);
  const [metodo1, metodo2] = metodosMaisUsados(d, hoje);
  const mesPassado = MESES[(hoje.getMonth() + 11) % 12];

  return {
    lancamento: [
      // Dia da semana + lugar + método.
      lugar1 && metodo1 ? `terça gastei 50 em ${lugar1} no ${metodo1}` : g.lancamento[0],
      // Lugar + valor com centavos + método.
      lugar2 && (metodo2 ?? metodo1)
        ? `${lugar2} 187,40 no ${metodo2 ?? metodo1}`
        : g.lancamento[1],
      // Parcelamento: continua genérico. Nada do que ela tem ensina "3x de".
      g.lancamento[2],
    ],
    chat: [
      g.chat[0],
      g.chat[1],
      cat1 ? `quanto gastei em ${cat1}?` : g.chat[2],
    ],
    busca: [
      // A segunda categoria: a primeira já está no chip do chat.
      (cat2 ?? cat1) ? `eu gastei algo em ${cat2 ?? cat1} esse mês?` : g.busca[0],
      // O mês passado, e não "dezembro" o ano inteiro.
      `quais os parcelamentos que fiz em ${mesPassado}?`,
      g.busca[2],
    ],
    meta: [
      g.meta[0],
      catDoLimite
        ? `não quero gastar mais de 500 por mês com ${catDoLimite} pelos próximos 6 meses`
        : g.meta[1],
      g.meta[2],
    ],
  };
};

/**
 * Os exemplos do campo de dívidas, com o nome de uma dívida dela.
 *
 * Separado dos outros porque quem tem a lista de dívidas é a seção de
 * Dívidas, e não o App.
 */
export const EXEMPLOS_DE_DIVIDA = [
  'fiz um consignado de 53 mil em 36x de 1950',
  'de onde sai a parcela do consignado?',
  'quanto sobra pra ir adiantando parcela?',
  'quero juntar 500 por mês pra amortizar',
];

export const exemplosDeDivida = (nomes: string[]): string[] => {
  const nome = nomes.map(n => String(n ?? '').trim()).find(serveDeExemplo);
  if (!nome) return EXEMPLOS_DE_DIVIDA;
  return [
    // Registrar continua genérico: é o exemplo de como CADASTRAR uma nova.
    EXEMPLOS_DE_DIVIDA[0],
    `de onde sai a parcela de ${nome}?`,
    `quanto falta pagar de ${nome}?`,
    EXEMPLOS_DE_DIVIDA[3],
  ];
};

/*
 * ── O encanamento ──────────────────────────────────────────────────────────
 *
 * Uma loja pequena em vez de props ou de contexto. Os campos que usam os
 * exemplos estão espalhados (o de lançamento mora dentro do formulário, o
 * chat flutua fora do quadro), e os dados moram no `AppContent`. Passar por
 * props atravessaria cinco componentes que não querem saber disso; um
 * provedor exigiria embrulhar um JSX de oitocentas linhas.
 *
 * Quem não publicou nada lê os genéricos: os testes de componente e a tela
 * antes de os dados chegarem funcionam sem montar loja nenhuma.
 */
let atuais: Exemplos = EXEMPLOS_GENERICOS;
const ouvintes = new Set<() => void>();

const publicar = (novos: Exemplos): void => {
  // Mesma lista, mesma referência: sem isto todo render do App acordaria
  // cada campo, e o chip piscaria enquanto ela digita.
  if (JSON.stringify(novos) === JSON.stringify(atuais)) return;
  atuais = novos;
  ouvintes.forEach(f => f());
};

/** O `AppContent` chama isto com o que já carregou. */
export const usePublicarExemplos = (d: DadosParaExemplos): void => {
  useEffect(() => {
    publicar(exemplosPara(d));
  }, [d.transactions, d.categories, d.paymentMethods]); // eslint-disable-line react-hooks/exhaustive-deps

  // Trocou de conta: os exemplos de uma pessoa não podem sobrar para a outra.
  useEffect(() => () => publicar(EXEMPLOS_GENERICOS), []);
};

/** Os campos chamam isto. */
export const useExemplos = (): Exemplos =>
  useSyncExternalStore(
    f => { ouvintes.add(f); return () => { ouvintes.delete(f); }; },
    () => atuais,
  );
